import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router";
import { AlertTriangle, Info, X } from "lucide-react";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import type {
  ReplenishmentProposalListOptions,
  ReplenishmentStageFilter,
} from "@shared-core/data/adapter";
import type {
  ReplenishmentMode,
  ReplenishmentProposalRow,
  ReplenishmentProposalState,
  StockOwner,
} from "@shared-core/data/api-types";
import { ApiError } from "@shared-core/data/api/errors";
import {
  formatCount,
  formatDateTimeOrDash,
} from "@shared-core/domain/formatters";
import {
  PROPOSAL_STATE_LABELS,
  REPLENISHMENT_MODE_LABELS,
  STAGE_BADGES,
  STAGE_FILTER_LABELS,
  STAGE_LABELS,
  expectedArrivalLabel,
  proposalWaitingLabel,
} from "@shared-core/domain/stock-replenishment";
import { useDebouncedValue } from "@shared-core/domain/useDebouncedValue";
import { paths } from "@shared-core/routing/routes";
import { ListPagination } from "../../app/components/patterns/ListPagination";
import { Badge } from "../../app/components/primitives/badge";
import { Button } from "../../app/components/primitives/button";
import { Label } from "../../app/components/primitives/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../app/components/primitives/table";
import { selectClassName } from "./StockFormField";
import { StockTabs } from "./StockTabs";

// Server-side filter, search, sort and paging (docs/list-endpoints.md):
// every change refetches and returns to page 1; rows render as returned.

const PAGE_SIZE = 25;

const SORT_OPTIONS = [
  { value: "created_at", label: "Oldest first" },
  { value: "-created_at", label: "Newest first" },
  { value: "stock_code,created_at", label: "Stock code" },
];

const STATE_BADGES: Record<
  ReplenishmentProposalState,
  "warning" | "success" | "neutral" | "error"
> = {
  open: "warning",
  raised: "success",
  dismissed: "neutral",
  failed: "error",
};

/** The Stock page's Replenishment tab: proposals to top items up, oldest
 * open first, for a person to confirm or dismiss (advise). */
export function ReplenishmentProposalsPage() {
  const dataAdapter = useDataAdapter();
  const navigate = useNavigate();

  const [rows, setRows] = useState<ReplenishmentProposalRow[]>([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [firstLoad, setFirstLoad] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebouncedValue(search, 300);
  const [state, setState] = useState<ReplenishmentProposalState | "">("open");
  const [mode, setMode] = useState<ReplenishmentMode | "">("");
  const [stage, setStage] = useState<ReplenishmentStageFilter | "">("");
  const [owner, setOwner] = useState<StockOwner | null>(null);
  const [sort, setSort] = useState(SORT_OPTIONS[0]!.value);
  const [page, setPage] = useState(1);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, state, mode, stage, owner, sort]);

  useEffect(() => {
    let cancelled = false;
    const options: ReplenishmentProposalListOptions = {
      page,
      pageSize: PAGE_SIZE,
      sort,
    };
    if (debouncedSearch.trim()) options.q = debouncedSearch.trim();
    if (state) options.state = state;
    if (mode) options.mode = mode;
    if (stage) options.stage = stage;
    if (owner) options.ownerCustomerId = owner.customer_id;
    setLoadError(false);
    dataAdapter
      .listReplenishmentProposals(options)
      .then((result) => {
        if (cancelled) return;
        setRows(result.items);
        setTotal(result.total);
        setTotalPages(result.totalPages);
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        console.error("stock.replenishment.proposals_load_failed", {
          status: error instanceof ApiError ? error.status : undefined,
        });
        setLoadError(true);
      })
      .finally(() => {
        if (!cancelled) setFirstLoad(false);
      });
    return () => {
      cancelled = true;
    };
  }, [dataAdapter, page, debouncedSearch, state, mode, stage, owner, sort]);

  const isFiltered =
    Boolean(debouncedSearch.trim()) ||
    state !== "open" ||
    Boolean(mode) ||
    Boolean(stage) ||
    owner != null;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl">Stock</h1>
        <p className="text-sm text-muted-foreground">
          Replenishment proposals: items whose projected stock fell to their
          re-order level, waiting for a person to confirm or dismiss.
        </p>
      </div>

      <StockTabs active="replenishment" />

      <div
        role="note"
        className="flex items-start gap-2 text-sm p-3 rounded-lg border border-sky-300 bg-sky-50 text-sky-900 dark:bg-sky-900/20 dark:border-sky-800 dark:text-sky-200"
      >
        <Info className="size-4 shrink-0 mt-0.5" />
        An item Tharstern still reorders is recorded here as a dry run: nothing
        is ordered for it until its owner is cut over.
      </div>

      <div className="flex flex-wrap items-end gap-4">
        <div className="space-y-1 w-full sm:w-60">
          <Label htmlFor="proposals-search">Stock code</Label>
          <input
            id="proposals-search"
            type="search"
            placeholder="Search stock code…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="px-3 py-2 border border-border rounded bg-background text-sm w-full"
          />
        </div>
        <OwnerFilter value={owner} onChange={setOwner} />
        <div className="space-y-1">
          <Label htmlFor="proposals-state">State</Label>
          <select
            id="proposals-state"
            className={`${selectClassName} w-auto`}
            value={state}
            onChange={(e) =>
              setState(e.target.value as ReplenishmentProposalState | "")
            }
          >
            <option value="">All states</option>
            {(
              Object.keys(PROPOSAL_STATE_LABELS) as ReplenishmentProposalState[]
            ).map((s) => (
              <option key={s} value={s}>
                {PROPOSAL_STATE_LABELS[s]}
              </option>
            ))}
          </select>
        </div>
        <div className="space-y-1">
          <Label htmlFor="proposals-stage">Stage</Label>
          <select
            id="proposals-stage"
            className={`${selectClassName} w-auto`}
            value={stage}
            onChange={(e) =>
              setStage(e.target.value as ReplenishmentStageFilter | "")
            }
          >
            <option value="">Any stage</option>
            {(
              Object.keys(STAGE_FILTER_LABELS) as ReplenishmentStageFilter[]
            ).map((option) => (
              <option key={option} value={option}>
                {STAGE_FILTER_LABELS[option]}
              </option>
            ))}
          </select>
        </div>
        <div className="space-y-1">
          <Label htmlFor="proposals-mode">Mode</Label>
          <select
            id="proposals-mode"
            className={`${selectClassName} w-auto`}
            value={mode}
            onChange={(e) => setMode(e.target.value as ReplenishmentMode | "")}
          >
            <option value="">Any mode</option>
            <option value="advise">{REPLENISHMENT_MODE_LABELS.advise}</option>
            <option value="automatic">
              {REPLENISHMENT_MODE_LABELS.automatic}
            </option>
          </select>
        </div>
        <div className="space-y-1 sm:ml-auto">
          <Label htmlFor="proposals-sort">Sort by</Label>
          <select
            id="proposals-sort"
            className={`${selectClassName} w-auto`}
            value={sort}
            onChange={(e) => setSort(e.target.value)}
          >
            {SORT_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {firstLoad ? (
        <div className="flex items-center justify-center min-h-[200px]">
          <div className="text-muted-foreground">Loading proposals…</div>
        </div>
      ) : loadError ? (
        <div
          role="alert"
          className="flex items-center gap-2 text-sm p-4 border border-red-300 dark:border-red-800 bg-red-50 dark:bg-red-900/20 rounded-lg text-red-700 dark:text-red-300"
        >
          <AlertTriangle className="size-4 shrink-0" />
          Couldn't load replenishment proposals. Please try again.
        </div>
      ) : rows.length === 0 ? (
        <div className="text-muted-foreground text-sm p-8 text-center border border-border rounded-lg">
          {isFiltered
            ? "No proposals match these filters."
            : "Nothing is waiting. The sweep proposes a reprint when an item's projected stock reaches its re-order level."}
        </div>
      ) : (
        <>
          <div className="text-sm text-muted-foreground">
            Showing {formatCount((page - 1) * PAGE_SIZE + 1)}–
            {formatCount(Math.min(page * PAGE_SIZE, total))} of{" "}
            {formatCount(total)} proposals
          </div>
          <div className="rounded-lg border border-border bg-surface overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Proposed</TableHead>
                  <TableHead>Stock item</TableHead>
                  <TableHead>Owner</TableHead>
                  <TableHead className="text-right">
                    Projected / level
                  </TableHead>
                  <TableHead className="text-right">Qty</TableHead>
                  <TableHead>Why it's waiting</TableHead>
                  <TableHead>State</TableHead>
                  <TableHead>Stage</TableHead>
                  <TableHead>Arrival</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((row) => (
                  <TableRow
                    key={row.id}
                    className="cursor-pointer"
                    onClick={() =>
                      navigate(
                        paths.stockReplenishmentProposal({
                          proposalId: row.id,
                        }),
                      )
                    }
                  >
                    <TableCell className="whitespace-nowrap">
                      <Link
                        to={paths.stockReplenishmentProposal({
                          proposalId: row.id,
                        })}
                        className="hover:underline"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {formatDateTimeOrDash(row.created_at)}
                      </Link>
                    </TableCell>
                    <TableCell className="font-medium">
                      {row.stock_code}
                    </TableCell>
                    <TableCell className="text-sm">
                      {row.owner_name ?? "—"}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {formatCount(row.projected)} /{" "}
                      {row.reorder_level == null
                        ? "–"
                        : formatCount(row.reorder_level)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {formatCount(
                        row.confirmed_quantity ?? row.proposed_quantity,
                      )}
                    </TableCell>
                    <TableCell className="text-sm">
                      {proposalWaitingLabel(row)}
                    </TableCell>
                    <TableCell>
                      <Badge variant={STATE_BADGES[row.state]}>
                        {PROPOSAL_STATE_LABELS[row.state]}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {row.stage ? (
                        <Badge variant={STAGE_BADGES[row.stage]}>
                          {STAGE_LABELS[row.stage]}
                        </Badge>
                      ) : (
                        "—"
                      )}
                    </TableCell>
                    <TableCell className="text-sm whitespace-nowrap">
                      {row.expected_arrival_on ? (
                        <>
                          {expectedArrivalLabel(
                            row.expected_arrival_on,
                            row.expected_arrival_confidence,
                          )}{" "}
                          {row.expected_arrival_confidence && (
                            <span className="text-xs text-muted-foreground">
                              {row.expected_arrival_confidence}
                            </span>
                          )}
                        </>
                      ) : (
                        "—"
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          <ListPagination
            page={page}
            totalPages={totalPages}
            onPageChange={setPage}
          />
        </>
      )}
    </div>
  );
}

/** Owner filter: search the stock owners (server-side) and pick one. */
function OwnerFilter({
  value,
  onChange,
}: {
  value: StockOwner | null;
  onChange: (owner: StockOwner | null) => void;
}) {
  const dataAdapter = useDataAdapter();
  const [query, setQuery] = useState("");
  const debouncedQuery = useDebouncedValue(query, 250);
  const [results, setResults] = useState<StockOwner[]>([]);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    dataAdapter
      .searchStockOwners(debouncedQuery.trim())
      .then((found) => !cancelled && setResults(found))
      .catch((error: unknown) => {
        console.error("stock.owners.search_failed", {
          status: error instanceof ApiError ? error.status : undefined,
        });
      });
    return () => {
      cancelled = true;
    };
  }, [dataAdapter, debouncedQuery, isOpen]);

  if (value) {
    return (
      <div className="space-y-1">
        <Label>Owner</Label>
        <div className="flex items-center gap-1 rounded border border-border bg-background px-2 py-1.5 text-sm">
          {value.name}
          <Button
            size="sm"
            variant="ghost"
            className="h-6 w-6 p-0"
            aria-label={`Clear owner ${value.name}`}
            onClick={() => onChange(null)}
          >
            <X className="size-3.5" />
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="relative space-y-1">
      <Label htmlFor="proposals-owner">Owner</Label>
      <input
        id="proposals-owner"
        type="search"
        placeholder="Any owner"
        autoComplete="off"
        value={query}
        onFocus={() => setIsOpen(true)}
        onChange={(e) => {
          setQuery(e.target.value);
          setIsOpen(true);
        }}
        className="px-3 py-2 border border-border rounded bg-background text-sm w-full sm:w-52"
      />
      {isOpen && results.length > 0 && (
        <ul
          role="listbox"
          aria-label="Owners"
          className="absolute z-10 mt-1 w-64 rounded border border-border bg-background shadow-md max-h-64 overflow-y-auto"
        >
          {results.map((o) => (
            <li key={o.customer_id}>
              <button
                type="button"
                role="option"
                aria-selected={false}
                className="w-full text-left px-3 py-2 text-sm hover:bg-muted"
                onClick={() => {
                  onChange(o);
                  setQuery("");
                  setIsOpen(false);
                }}
              >
                {o.name}
                <span className="block text-xs text-muted-foreground">
                  {o.customer_code}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
